import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useBanners } from './BannerContext';
import highIcon from './icons/high_priority_banner.svg';
import mediumIcon from './icons/medium_priority_banner.svg';
import lowIcon from './icons/low_priority_banner.svg';

const DISMISSED_KEY = 'wv_dismissed_banners';

const getDismissedMap = () => {
    try {
        const raw = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '{}');
        // Accept object format only; discard legacy array format
        return raw && !Array.isArray(raw) && typeof raw === 'object' ? raw : {};
    } catch {
        return {};
    }
};

const saveDismissedMap = (map) => {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
};

const PRIORITY_STYLES = {
    High: {
        bg: '#FBEDEC',
        borderColor: '#D84C3A',
        accentColor: '#D84C3A',
        titleColor: '#D84C3A',
        icon: highIcon,
    },
    Medium: {
        bg: '#FFFAF2',
        borderColor: '#ECB445',
        accentColor: '#ECB445',
        titleColor: '#9F782B',
        icon: mediumIcon,
    },
    Low: {
        bg: '#FAFFF3',
        borderColor: '#8DC63F',
        accentColor: '#8DC63F',
        titleColor: '#5F8E1E',
        icon: lowIcon,
    },
};

const BODY_COLOR = '#334F61';

const BannerItem = ({ banner, onDismiss }) => {
    const style = PRIORITY_STYLES[banner.priority] || PRIORITY_STYLES.Low;
    const [expanded, setExpanded] = useState(false);
    const hasLongBody = banner.body && banner.body.length > 120;
    const bodyPreview =
        hasLongBody && !expanded
            ? banner.body.slice(0, 120) + '…'
            : banner.body;

    return (
        <div
            className='sb-banner-item'
            style={{
                background: style.bg,
                borderLeft: `4px solid ${style.borderColor}`,
            }}
        >
            <img
                className='sb-banner-item__icon'
                src={style.icon}
                alt={`${banner.priority} priority`}
            />

            <div className='sb-banner-item__content'>
                <div
                    className='sb-banner-item__title'
                    style={{ color: style.titleColor }}
                >
                    {banner.title}
                </div>
                {banner.body && (
                    <div
                        className='sb-banner-item__body'
                        style={{ color: BODY_COLOR }}
                    >
                        {bodyPreview}
                        {hasLongBody && (
                            <button
                                className='sb-banner-item__toggle'
                                onClick={() => setExpanded((e) => !e)}
                            >
                                {expanded ? 'Show less' : 'Show more'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className='sb-banner-item__right'>
                <span
                    className='sb-banner-item__sev'
                    style={{ color: style.accentColor }}
                >
                    <span
                        className='sb-banner-item__sev-dot'
                        style={{ background: style.accentColor }}
                    />
                    {banner.priority.toUpperCase()}
                </span>
                <button
                    className='sb-banner-item__dismiss'
                    onClick={() => onDismiss(banner)}
                    title='Dismiss'
                    aria-label='Dismiss banner'
                    style={{ color: BODY_COLOR }}
                >
                    &times;
                </button>
            </div>
        </div>
    );
};

const SupportBannerDisplay = () => {
    const { banners: allBanners } = useBanners();
    const [dismissedMap, setDismissedMap] = useState(() => getDismissedMap());
    const [ready, setReady] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    const bannerRef = useRef(null);

    // A banner reappears if it was never dismissed, or if its content changed since dismissal
    const visibleBanners = allBanners.filter(
        (b) => !dismissedMap[b.id] || dismissedMap[b.id] !== b.lastEditedAt
    );

    // Reset anchor height when all banners are removed so no empty space remains
    useEffect(() => {
        if (visibleBanners.length === 0 && anchorRef.current) {
            anchorRef.current.style.height = '0px';
            document.documentElement.style.setProperty(
                '--sb-banner-height',
                '0px'
            );
        }
    }, [visibleBanners.length]);

    // Clean up CSS variable on unmount
    useEffect(() => {
        return () =>
            document.documentElement.style.removeProperty('--sb-banner-height');
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setReady(true), 800);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useLayoutEffect(() => {
        const measure = () => {
            if (!anchorRef.current) return;
            const anchorRect = anchorRef.current.getBoundingClientRect();
            const ktContent = document.getElementById('kt_content');
            const top = ktContent
                ? ktContent.getBoundingClientRect().top
                : anchorRect.top;
            setPos({ top, left: anchorRect.left, width: anchorRect.width });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    useEffect(() => {
        if (!bannerRef.current || !anchorRef.current) return;
        const ro = new ResizeObserver(([entry]) => {
            const h = entry.contentRect.height;
            if (anchorRef.current) anchorRef.current.style.height = h + 'px';
            document.documentElement.style.setProperty(
                '--sb-banner-height',
                h + 'px'
            );
        });
        ro.observe(bannerRef.current);
        return () => ro.disconnect();
    });

    if (isMobile) return null;

    const handleDismiss = (banner) => {
        setDismissedMap((prev) => {
            const next = { ...prev, [banner.id]: banner.lastEditedAt };
            saveDismissedMap(next);
            return next;
        });
    };

    const bannerUI =
        ready && visibleBanners.length > 0 && pos
            ? ReactDOM.createPortal(
                  <div
                      ref={bannerRef}
                      className='sb-container'
                      data-testid='sb-container'
                      style={{
                          position: 'fixed',
                          top: pos.top,
                          left: pos.left,
                          width: pos.width,
                          zIndex: 96,
                      }}
                  >
                      {visibleBanners.map((banner) => (
                          <BannerItem
                              key={banner.id}
                              banner={banner}
                              onDismiss={handleDismiss}
                          />
                      ))}
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <div
                ref={anchorRef}
                data-testid='sb-anchor'
                style={{
                    height: 0,
                    overflow: 'visible',
                    pointerEvents: 'none',
                }}
            />
            {bannerUI}
        </>
    );
};

export default SupportBannerDisplay;
